'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionTitle from '@/components/ui/SectionTitle';
import type { RequisitionStage, RequisitionStatus, Priority } from '@/lib/mock-data';
import { connectionsByRegion } from '@/lib/mock-data';
import {
  Search,
  Download,
  CheckCircle2,
  Clock,
  FileText,
  Truck,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { computeReconciliation, getHeldContractors } from '@/lib/reconciliation';
import {
  defaultRatioMap,
  defaultBlendWeights,
  defaultCommissioningLagDays,
} from '@/lib/reconciliation-config';

// Map from English item description to itemCode for translation lookup
const DESCRIPTION_TO_CODE: Record<string, string> = {
  'Distribution Transformer 100 KVA': 'DT-100KVA',
  'Distribution Transformer 315 KVA': 'DT-315KVA',
  'Distribution Transformer 500 KVA': 'DT-500KVA',
  'LV Cable 25mm² Armoured': 'LVC-25MM',
  'LV Cable 70mm² Armoured': 'LVC-70MM',
  'Prepaid Meter STS Single Phase': 'PPM-STS',
  'Prepaid Meter 3-Phase Commercial': 'PPM-3PH',
  'ACSR Dog Conductor': 'ACSR-Dog',
  'ACSR Rabbit Conductor': 'ACSR-Rabbit',
  'Grounding Rod 16mm Copper Clad': 'GRD-ROD-16',
  'Grounding Rod 20mm Copper Clad': 'GRD-ROD-20',
  'Service Drop Cable 16mm²': 'SVC-DROP',
};

const STAGE_KEYS: { key: RequisitionStage; labelKey: string; shortKey: string; icon: React.ElementType }[] = [
  { key: 'consultant_review', labelKey: 'stage.consultant_review', shortKey: 'stage.short.consultant_review', icon: FileText },
  { key: 'signed_hardcopy', labelKey: 'stage.signed_hardcopy', shortKey: 'stage.short.signed_hardcopy', icon: CheckCircle2 },
  { key: 'chief_director_approval', labelKey: 'stage.chief_director_approval', shortKey: 'stage.short.chief_director_approval', icon: Clock },
  { key: 'main_stock_release', labelKey: 'stage.main_stock_release', shortKey: 'stage.short.main_stock_release', icon: Truck },
];

const stageOrder: Record<RequisitionStage, number> = {
  consultant_review: 0,
  signed_hardcopy: 1,
  chief_director_approval: 2,
  main_stock_release: 3,
};

export default function RequisitionsPage() {
  const { requisitions, profile } = useCountry();
  const { t, fmtNum, fmtDate } = useLanguage();

  const [stageFilter, setStageFilter] = useState<RequisitionStage | ''>('');
  const [statusFilter, setStatusFilter] = useState<RequisitionStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<Priority | ''>('');
  const [search, setSearch] = useState('');

  // Compute held contractors using config defaults (no user-adjustable ratios on this page)
  const heldContractors = useMemo(() => {
    const result = computeReconciliation({
      requisitions,
      regional: connectionsByRegion,
      ratios: defaultRatioMap,
      weights: defaultBlendWeights,
      lagDays: defaultCommissioningLagDays,
    });
    return getHeldContractors(result.rows);
  }, [requisitions]);

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    STAGE_KEYS.forEach((s) => {
      counts[s.key] = requisitions.filter((r) => r.stage === s.key).length;
    });
    return counts;
  }, [requisitions]);

  const filtered = useMemo(() => {
    let items = [...requisitions];
    if (stageFilter) items = items.filter((r) => r.stage === stageFilter);
    if (statusFilter) items = items.filter((r) => r.status === statusFilter);
    if (priorityFilter) items = items.filter((r) => r.priority === priorityFilter);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(
        (r) =>
          r.moenId.toLowerCase().includes(q) ||
          r.contractor.toLowerCase().includes(q) ||
          r.department.toLowerCase().includes(q) ||
          r.itemDescription.toLowerCase().includes(q)
      );
    }
    return items.sort((a, b) => stageOrder[a.stage] - stageOrder[b.stage]);
  }, [requisitions, stageFilter, statusFilter, priorityFilter, search]);

  const urgentCount = requisitions.filter((r) => r.priority === 'urgent' && r.status === 'pending_approval').length;

  return (
    <AppLayout
      title={t('page.requisitionsTitle')}
      subtitle={t('page.requisitionsSub')}
    >
      {/* Hold Banner */}
      {heldContractors.length > 0 && (
        <div
          className="flex items-center gap-3 rounded-xl px-4 py-3 mb-4 border"
          style={{
            backgroundColor: 'rgba(220,38,38,0.06)',
            borderColor: 'rgba(220,38,38,0.2)',
          }}
        >
          <ShieldAlert size={16} style={{ color: 'var(--status-red)' }} />
          <p className="text-sm font-medium text-status-red">
            <span className="font-bold">{heldContractors.length}</span>{' '}
            {t('recon.holdBanner')}:{' '}
            <span className="font-semibold">{heldContractors.join(', ')}</span>
          </p>
        </div>
      )}

      {/* Urgent Alert Banner */}
      {urgentCount > 0 && (
        <div
          className="flex items-center gap-3 rounded-xl px-4 py-3 mb-5 border"
          style={{
            backgroundColor: 'rgba(220,38,38,0.06)',
            borderColor: 'rgba(220,38,38,0.2)',
          }}
        >
          <AlertTriangle size={16} style={{ color: 'var(--status-red)' }} />
          <p className="text-sm font-medium text-status-red">
            {urgentCount} {t('common.urgentPending')}
          </p>
          <button
            onClick={() => setStageFilter('chief_director_approval')}
            className="ml-auto text-xs font-semibold text-status-red underline underline-offset-2 hover:no-underline"
          >
            Filter to CD Approval
          </button>
        </div>
      )}

      {/* Pipeline Flow Bar */}
      <div className="bg-surface-card rounded-xl border border-surface-border shadow-card p-5 mb-5">
        <SectionTitle
          title="Approval Pipeline Status"
          subtitle="Click a stage to filter the table below"
        />
        <div className="flex items-stretch gap-0 mt-2 overflow-x-auto">
          {STAGE_KEYS.map((stage, idx) => {
            const count = stageCounts[stage.key] || 0;
            const isActive = stageFilter === stage.key;
            const IconComp = stage.icon;
            const isLast = idx === STAGE_KEYS.length - 1;

            return (
              <React.Fragment key={`pipeline-${stage.key}`}>
                <button
                  onClick={() => setStageFilter(isActive ? '' : stage.key)}
                  className={`flex-1 min-w-[130px] flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all duration-200 ${
                    isActive
                      ? 'border-accent text-white' : 'border-surface-border hover:border-accent/40 text-text-muted hover:text-text-main bg-surface-light'
                  }`}
                  style={
                    isActive
                      ? { backgroundColor: 'var(--primary)', borderColor: 'var(--accent)' }
                      : {}
                  }
                >
                  <IconComp
                    size={18}
                    className={isActive ? 'text-accent' : ''}
                    style={isActive ? { color: 'var(--accent)' } : {}}
                  />
                  <span className="text-xs font-semibold text-center leading-tight">
                    {t(stage.shortKey)}
                  </span>
                  <span
                    className={`text-xl font-bold tabular-nums ${
                      isActive ? 'text-white' : 'text-text-main'
                    }`}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {count}
                  </span>
                  <span className="text-[10px] opacity-70">requisitions</span>
                </button>
                {!isLast && (
                  <div className="flex items-center px-1">
                    <ChevronRight size={16} className="text-text-muted flex-shrink-0" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-surface-card rounded-xl border border-surface-border shadow-card">
        <div className="p-5 border-b border-surface-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <SectionTitle
              title="Requisition Register"
              subtitle={`${filtered.length} requisitions`}
            />
            <div className="flex items-center gap-2">
              {/* Priority filter */}
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as Priority | '')}
                className="text-xs border border-surface-border rounded-lg px-2.5 py-1.5 bg-surface-light text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30"
              >
                <option value="">{t('filter.allPriorities')}</option>
                <option value="urgent">{t('priority.urgent')}</option>
                <option value="high">{t('priority.high')}</option>
                <option value="normal">{t('priority.normal')}</option>
                <option value="low">{t('priority.low')}</option>
              </select>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as RequisitionStatus | '')}
                className="text-xs border border-surface-border rounded-lg px-2.5 py-1.5 bg-surface-light text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30"
              >
                <option value="">{t('filter.allStatus')}</option>
                <option value="pending_approval">{t('status.pending_approval')}</option>
                <option value="director_approved">{t('status.director_approved')}</option>
                <option value="released">{t('status.released')}</option>
              </select>

              <button
                onClick={() => toast.success(t('toast.exported'))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                <Download size={12} />
                {t('action.export')}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-3 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              placeholder="Search by MOEN ID, contractor, or item..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-surface-light border border-surface-border text-sm rounded-lg pl-8 pr-3 py-1.5 text-text-main placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[
                  t('table.requisitionId'),
                  t('table.contractor'),
                  `${t('table.department')} / ${t('table.region')}`,
                  t('table.item'),
                  t('table.qtyRequested'),
                  t('table.warehouse'),
                  t('table.hardcopyRef'),
                  t('table.stage'),
                  t('table.priority'),
                  t('table.status'),
                  t('table.submitted'),
                  t('table.approved'),
                ].map((col) => (
                  <th
                    key={`req-th-${col}`}
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-text-muted"
                    style={{
                      letterSpacing: '0.06em',
                      backgroundColor: 'var(--background)',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FileText size={32} className="text-text-muted opacity-30" />
                      <p className="text-sm font-medium text-text-muted">
                        {t('common.noResults')}
                      </p>
                      <button
                        onClick={() => {
                          setStageFilter('');
                          setStatusFilter('');
                          setPriorityFilter('');
                          setSearch('');
                        }}
                        className="text-xs font-medium underline underline-offset-2"
                        style={{ color: 'var(--accent)' }}
                      >
                        Clear all filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((req, idx) => {
                  const isHeld = heldContractors.includes(req.contractor);
                  return (
                    <tr
                      key={req.id}
                      className="transition-colors hover:bg-surface-light group"
                      style={{
                        borderBottom:
                          idx < filtered.length - 1
                            ? '1px solid var(--border)'
                            : 'none',
                      }}
                    >
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span
                          className="font-mono text-xs font-bold"
                          style={{ color: 'var(--primary)' }}
                        >
                          {req.moenId}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-text-main max-w-[160px]">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="truncate text-xs">{req.contractor}</span>
                          {isHeld && (
                            <span
                              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0"
                              style={{
                                backgroundColor: 'rgba(220,38,38,0.12)',
                                color: 'var(--status-red)',
                              }}
                            >
                              {t('recon.onHold')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                        <div>{req.department}</div>
                        <div
                          className="text-[10px] mt-0.5 font-medium"
                          style={{ color: 'var(--geo)' }}
                        >
                          {req.region}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-text-main max-w-[160px]">
                        <span className="truncate block">
                          {(() => {
                            const code = DESCRIPTION_TO_CODE[req.itemDescription];
                            return code ? t(`material.${code}`) : req.itemDescription;
                          })()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs font-semibold text-text-main tabular-nums whitespace-nowrap">
                        {fmtNum(req.quantityRequested)} {t(`common.${req.unit}`)}
                      </td>
                      <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                        {req.warehouse}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-[11px] text-text-muted">
                          {req.hardcopyRef}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-md"
                          style={{
                            backgroundColor:
                              req.stage === 'main_stock_release' ? 'rgba(34,160,107,0.1)'
                                : req.stage === 'chief_director_approval' ? 'rgba(220,38,38,0.08)' : 'rgba(13,59,46,0.08)',
                            color:
                              req.stage === 'main_stock_release' ? 'var(--status-green)'
                                : req.stage === 'chief_director_approval' ? 'var(--status-red)' : 'var(--primary)',
                          }}
                        >
                          {t(`stage.short.${req.stage}`)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            req.priority === 'urgent' ? 'bg-[color:var(--status-red)]/10 text-status-red'
                              : req.priority === 'high' ? 'bg-[color:var(--status-amber)]/10 text-status-amber' : 'bg-muted text-text-muted'
                          }`}
                        >
                          {t(`priority.${req.priority}`)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={req.status} label={t(`status.${req.status}`)} />
                      </td>
                      <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                        {fmtDate(req.submittedDate)}
                      </td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap">
                        {req.approvalDate ? (
                          <span className="text-status-green font-medium">
                            {fmtDate(req.approvalDate)}
                          </span>
                        ) : (
                          <span className="text-text-muted italic">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex items-center justify-between text-xs text-text-muted"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <span>
            {t('common.showing')}{' '}
            <span className="font-semibold text-text-main">{filtered.length}</span> {t('common.of')}{' '}
            <span className="font-semibold text-text-main">{requisitions.length}</span>{' '}
            {t('common.results')}
          </span>
          <span>
            Pending CD Approvals:{' '}
            <span className="font-bold text-status-red">
              {requisitions.filter((r) => r.stage === 'chief_director_approval').length}
            </span>
          </span>
        </div>
      </div>
    </AppLayout>
  );
}